import ApiInstance from "./ApiInstance";
import { Config } from "../Config";

export type VerificationState = "pending" | "needs_info" | "interview" | "verified" | "rejected";

export interface AdminReviewNote {
  at: string;
  note: string;
}

export interface AdminPsychologist {
  id: string;
  displayName: string;
  email?: string;
  specializations: string[];
  languages: string[];
  regionsServed: string[];
  rateCents?: number;
  profession?: string;
  visaNumber?: string;
  erkenningNumber?: string;
  rizivNumber?: string;
  psychologenCommissieNumber?: string;
  professionalAssociation?: string;
  associationMemberNumber?: string;
  psychotherapyLegalBasis?: string;
  // DAC7 / BTW intake (raw rijksregisternummer never leaves the API).
  businessForm?: string;
  vatStatus?: string;
  vatNumber?: string;
  kboNumber?: string;
  birthDate?: string;
  tinProvided?: boolean;
  eligibleFundCodes: string[];
  documentCount: number;
  reviewNotes: AdminReviewNote[];
  verificationCheck?: { checkedSources: string[]; checkedAt: string };
  verifiedState: VerificationState;
  createdAt: string;
}

export interface VerifyOpts {
  note?: string;
  checkedSources?: string[];
}

export interface AdminDoc {
  id: string;
  kind: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  createdAt: string;
}

export interface AdminOrg {
  id: string;
  name: string;
  inviteCode: string;
  seatLimit?: number;
  status: string;
  fundingSuspended?: boolean;
  contractStartDate?: string;
  adminCount: number;
  admins: { id: string; email: string }[];
  memberCount: number;
  fairUseCapPerMember?: number;
  kboNumber?: string;
  vatNumber?: string;
}

// --- Automatisering (takenregister + jobrunner + compliance-jaarkalender) ---
export interface AutomationTaskRun {
  at: string;
  ok: boolean;
  dryRun?: boolean;
  errors?: string[];
  counters?: Record<string, unknown>;
}

export interface AutomationTask {
  id: string;
  name: string;
  purpose: string;
  steps: string[];
  reads: string[];
  writes: string[];
  externalDeps: string[];
  compliance: string[];
  cron?: { expression: string; timeZone: string; textNl: string; yaml: string } | null;
  dryRunSupported: boolean;
  taskModule: string;
  logSource: "TaskRunLog" | "SettlementRunLog" | null;
  calendarItemId?: string;
  manualInstruction?: string;
  adminJobs?: { dry?: string; apply?: string } | null;
  applyConfirmTextNl?: string;
  aiPrompt: string;
  sourceRefs: string[];
  lastRun: AutomationTaskRun | null;
  recentRuns: AutomationTaskRun[];
  nextRun: string | null;
  upcoming: string[];
  running: { runId: string; jobId: string; startedAt: string } | null;
  health: "running" | "ok" | "attention" | "failed" | "missed" | "manual";
}

export interface AutomationCalendarItem {
  id: string;
  title: string;
  description: string;
  cadence: "yearly" | "once" | "monthly_until_done";
  cycle: string;
  windowStart: string;
  deadline: string;
  state: "upcoming" | "open" | "overdue" | "done";
  doneAt?: string;
  remindedAt?: string;
  meta: {
    instruction: string;
    achtergrond: string[];
    commands?: string[];
    links?: { label: string; url: string }[];
    aiPrompt?: string;
    sourceRefs: string[];
    taskId?: string;
  } | null;
  history: { cycle: string; remindedAt?: string; doneAt?: string; doneBy?: string }[];
}

export interface AutomationOverview {
  now: string;
  tasks: AutomationTask[];
  calendar: AutomationCalendarItem[];
}

class AdminApi {
  async listPsychologists(state?: string): Promise<AdminPsychologist[]> {
    const { data } = await ApiInstance.getApi().get("/admin/psychologists", { params: state ? { state } : {} });
    return data.psychologists;
  }
  async verify(id: string, opts?: VerifyOpts): Promise<void> {
    await ApiInstance.getApi().post(`/admin/psychologists/${id}/verify`, { note: opts?.note, checkedSources: opts?.checkedSources });
  }
  async reject(id: string, opts?: VerifyOpts): Promise<void> {
    await ApiInstance.getApi().post(`/admin/psychologists/${id}/reject`, { note: opts?.note, checkedSources: opts?.checkedSources });
  }
  async setStatus(id: string, state: VerificationState, opts?: VerifyOpts): Promise<void> {
    await ApiInstance.getApi().post(`/admin/psychologists/${id}/status`, { state, note: opts?.note, checkedSources: opts?.checkedSources });
  }
  async addNote(id: string, note: string): Promise<void> {
    await ApiInstance.getApi().post(`/admin/psychologists/${id}/note`, { note });
  }
  async listDocuments(id: string): Promise<AdminDoc[]> {
    const { data } = await ApiInstance.getApi().get(`/admin/psychologists/${id}/documents`);
    return data.documents;
  }
  documentUrl(id: string, docId: string): string {
    return `${Config.getBackendUrl()}/admin/psychologists/${id}/documents/${docId}`;
  }
  async listOrgs(): Promise<AdminOrg[]> {
    const { data } = await ApiInstance.getApi().get("/admin/organisations");
    return data.organisations;
  }
  async createOrg(name: string, seatLimit?: number, fairUseCapPerMember?: number): Promise<{ id: string; inviteCode: string }> {
    const { data } = await ApiInstance.getApi().post("/admin/organisations", { name, seatLimit, fairUseCapPerMember });
    return data;
  }
  async addOrgAdmin(orgId: string, email: string): Promise<void> {
    await ApiInstance.getApi().post(`/admin/organisations/${orgId}/admins`, { email });
  }
  async updateOrg(orgId: string, body: { name?: string; seatLimit?: number | null; fairUseCapPerMember?: number; kboNumber?: string; vatNumber?: string; contractStartDate?: string }): Promise<void> {
    await ApiInstance.getApi().patch(`/admin/organisations/${orgId}`, body);
  }
  async removeOrgAdmin(orgId: string, userId: string): Promise<void> {
    await ApiInstance.getApi().delete(`/admin/organisations/${orgId}/admins/${userId}`);
  }
  // --- Overzicht / gebruikers / audit (admin-home, support, accountability) ---
  async overview(): Promise<{ users: { clients: number; psychologists: number }; vetting: { pending: number; verified: number }; organisations: { total: number; fundingSuspended: number; memberships: number }; billing: { openInvoices: number; lastSettlementRun: { year: number; month: number; ranAt: string; ok: boolean } | null }; compliance: { open: number; overdue: number } }> {
    const { data } = await ApiInstance.getApi().get("/admin/overview");
    return data;
  }
  async searchUsers(query: string): Promise<{ users: { id: string; email: string; name: string | null; role: string; mfaEnabled: boolean; restricted: boolean; createdAt: string; orgNames: string[] }[] }> {
    const { data } = await ApiInstance.getApi().get(`/admin/users?query=${encodeURIComponent(query)}`);
    return data;
  }
  async setUserRestriction(userId: string, active: boolean, reason?: string): Promise<void> {
    await ApiInstance.getApi().post(`/admin/users/${userId}/restriction`, { active, reason });
  }
  async auditLog(limit = 50, action = ""): Promise<{ entries: { at: string; action: string; actorRole?: string; targetType?: string; targetId?: string; outcome: string; ip?: string }[] }> {
    const { data } = await ApiInstance.getApi().get(`/admin/audit?limit=${limit}${action ? `&action=${encodeURIComponent(action)}` : ""}`);
    return data;
  }

  // --- Facturen (billing-admin) ---
  async listInvoices(): Promise<{ invoices: { id: string; number: string; type: string; status: string; counterparty: { kind: string; name: string }; period: { year: number; month?: number }; totals: { payableCents: number; inclCents: number }; peppolStatus?: string; issuedAt?: string }[] }> {
    const { data } = await ApiInstance.getApi().get("/admin/billing/invoices");
    return data;
  }
  invoiceHtmlPath(id: string): string { return `/admin/billing/invoices/${id}/html`; }
  async issueInvoice(id: string): Promise<void> { await ApiInstance.getApi().post(`/admin/billing/invoices/${id}/issue`, {}); }
  async markInvoicePaid(id: string): Promise<void> { await ApiInstance.getApi().post(`/admin/billing/invoices/${id}/mark-paid`, {}); }
  async sendInvoicePeppol(id: string): Promise<void> { await ApiInstance.getApi().post(`/admin/billing/invoices/${id}/send-peppol`, {}); }
  async runMonthlyInvoices(year: number, month: number): Promise<{ created: string[]; skipped: number }> {
    const { data } = await ApiInstance.getApi().post("/admin/billing/invoices/run", { year, month });
    return data;
  }
  async createAnnualInvoice(orgId: string, year: number): Promise<void> { await ApiInstance.getApi().post(`/admin/billing/organisations/${orgId}/annual-invoice`, { year }); }

  // --- DAC7 ---
  async dac7Completeness(year: number): Promise<{ incomplete: { profileId: string; displayName: string; missing: string[]; reminderCount?: number; suspended?: boolean }[] }> {
    const { data } = await ApiInstance.getApi().get(`/admin/billing/dac7/completeness/${year}`);
    return data;
  }
  async dac7Remind(profileId: string): Promise<void> { await ApiInstance.getApi().post(`/admin/billing/dac7/remind/${profileId}`, {}); }

  // --- Settlement-waakhond + reconciliatie ---
  async settlementRuns(): Promise<{ runs: { year: number; month: number; ranAt: string; ok: boolean; orgsSettled: number; payoutsCount: number; payoutCents: number; payoutsPendingCount: number; employerInvoicesCreated: number; providerInvoicesCreated: number; overdueCount: number; fundingSuspendedCount: number; runErrors: string[] }[] }> {
    const { data } = await ApiInstance.getApi().get("/admin/billing/settlement-runs");
    return data;
  }
  async reconciliation(year: number, month: number): Promise<{ rows: { orgName: string; invoiceNumber: string | null; invoiceStatus: string | null; invoicedPassThroughCents: number; usagePassThroughCents: number; diffCents: number; payoutDueCents: number; unpaidPayouts: number; unsettledUsage: number }[]; dac7: { considerationCents: number; sellers: number } }> {
    const { data } = await ApiInstance.getApi().get(`/admin/billing/reconciliation?year=${year}&month=${month}`);
    return data;
  }
  // --- Compliance-jaarkalender ---
  async complianceCalendar(): Promise<{ items: { id: string; title: string; description: string; cycle: string; windowStart: string; deadline: string; state: "upcoming" | "open" | "overdue" | "done"; doneAt?: string }[] }> {
    const { data } = await ApiInstance.getApi().get("/admin/compliance/calendar");
    return data;
  }
  async markComplianceDone(itemId: string): Promise<void> {
    await ApiInstance.getApi().post(`/admin/compliance/calendar/${itemId}/done`, {});
  }
  async markComplianceUndone(itemId: string): Promise<void> {
    await ApiInstance.getApi().delete(`/admin/compliance/calendar/${itemId}/done`);
  }
  // --- Automatisering ---
  async automationOverview(): Promise<AutomationOverview> {
    const { data } = await ApiInstance.getApi().get("/admin/automation/overview");
    return data;
  }
  async automationRun(job: string, confirm?: boolean): Promise<{ runId: string }> {
    const { data } = await ApiInstance.getApi().post("/admin/automation/run", { job, confirm });
    return data;
  }
  async automationRunLog(runId: string, offset: number): Promise<{ content: string; nextOffset: number; exists: boolean; running: boolean }> {
    const { data } = await ApiInstance.getApi().get(`/admin/automation/run/${runId}/log?offset=${offset}`);
    return data;
  }
  // --- Platform settings (private mode) ---
  async getSettings(): Promise<{ privateMode: boolean }> {
    const { data } = await ApiInstance.getApi().get("/admin/settings");
    return data;
  }
  async setPrivateMode(privateMode: boolean): Promise<void> {
    await ApiInstance.getApi().patch("/admin/settings", { privateMode });
  }
}

const adminApi = new AdminApi();
export default adminApi;
